import React, { useState, useEffect, useRef } from 'react';
import { StatusMenu } from './TasksPage';
import { supabase } from '../supabase';

const STATUSES = [
  { key: 'Draft', label: 'Draft', color: 'var(--text3)', bg: 'var(--bg3)' },
  { key: 'Active', label: 'Active', color: 'var(--green)', bg: 'var(--green-dim)' },
  { key: 'Done', label: 'Done', color: 'var(--blue)', bg: 'var(--blue-dim)' },
  { key: 'Parked', label: 'Cancelled', color: 'var(--text3)', bg: 'var(--bg3)' },
];

function statusStyle(status) {
  return STATUSES.find(s => s.key === status) || STATUSES[0];
}

function fmtDate(ts) {
  return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function CategoryPicker({ value, onChange, categories = [] }) {
  const selected = value ? value.split(',').map(s => s.trim()).filter(Boolean) : [];
  const toggle = (cat) => {
    const next = selected.includes(cat) ? selected.filter(c => c !== cat) : [...selected, cat];
    onChange(next.join(', '));
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {categories.map(cat => {
        const on = selected.includes(cat);
        return (
          <button key={cat} type="button" onClick={() => toggle(cat)} style={{
            padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
            background: on ? 'var(--accent)' : 'var(--bg3)',
            color: on ? '#fff' : 'var(--text2)',
            border: on ? 'none' : '1px solid var(--border2)',
          }}>{cat}</button>
        );
      })}
    </div>
  );
}

function CommentsPanel({ ideaId, user }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState('');

  const load = async () => {
    const { data } = await supabase.from('idea_comments').select('*').eq('idea_id', ideaId).order('created_at', { ascending: false });
    setComments(data || []);
  };

  useEffect(() => { load(); }, [ideaId]);

  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Unknown';
    await supabase.from('idea_comments').insert({ idea_id: ideaId, content: text.trim(), created_by: user?.id, created_by_name: userName });
    setText(''); await load(); setSaving(false);
  };

  const saveEdit = async (id) => {
    if (!editText.trim()) return;
    await supabase.from('idea_comments').update({ content: editText.trim(), updated_at: new Date().toISOString() }).eq('id', id);
    setEditing(null); await load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    await supabase.from('idea_comments').delete().eq('id', id); await load();
  };

  const fmt = (ts) => new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' · ' + new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  return (
    <div>
      <label style={{ fontSize: 12, color: 'var(--accent)', display: 'block', marginBottom: 12 }}>COMMENTS</label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <textarea rows={2} value={text} onChange={e => setText(e.target.value)} placeholder="Add a comment..." style={{ flex: 1, resize: 'vertical' }} />
        <button className="btn-primary" onClick={add} disabled={saving || !text.trim()} style={{ alignSelf: 'flex-end', whiteSpace: 'nowrap' }}>{saving ? '...' : 'Add'}</button>
      </div>
      {comments.length === 0 ? <p style={{ color: 'var(--text3)', fontSize: 13 }}>No comments yet.</p> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {comments.map(c => (
            <div key={c.id} style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '12px 14px', borderLeft: c.created_by === user?.id ? '3px solid var(--accent)' : '3px solid var(--border2)' }}>
              {editing === c.id ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <textarea rows={2} value={editText} onChange={e => setEditText(e.target.value)} style={{ flex: 1 }} autoFocus />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button className="btn-primary" style={{ padding: '5px 10px', fontSize: 12 }} onClick={() => saveEdit(c.id)}>Save</button>
                    <button className="btn-ghost" style={{ padding: '5px 10px', fontSize: 12 }} onClick={() => setEditing(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <p style={{ fontSize: 13, color: 'var(--text)', marginBottom: 8, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{c.content}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                      <strong style={{ color: 'var(--text2)' }}>{c.created_by_name}</strong> · {fmt(c.created_at)}
                    </div>
                    {c.created_by === user?.id && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn-ghost" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => { setEditing(c.id); setEditText(c.content); }}>Edit</button>
                        <button className="btn-danger" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => del(c.id)}>Delete</button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IdeaDetailModal({ idea, user, isAdmin, onClose, onRefresh, onCreateTask, onDelete, categories = [] }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(idea.title);
  const [description, setDescription] = useState(idea.description || '');
  const [category, setCategory] = useState(idea.category || '');
  const [status, setStatus] = useState(idea.status);
  const [saving, setSaving] = useState(false);
  const isOwner = idea.created_by === user?.id || isAdmin;
  const ss = statusStyle(idea.status);

  const save = async () => {
    setSaving(true);
    await supabase.from('ideas').update({ title, description, category, status, updated_at: new Date().toISOString() }).eq('id', idea.id);
    setSaving(false); setEditing(false); onRefresh();
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 620 }}>
        <div className="modal-header">
          {editing ? (
            <input value={title} onChange={e => setTitle(e.target.value)} style={{ fontSize: 15, fontWeight: 700, flex: 1, marginRight: 12 }} autoFocus />
          ) : (
            <h2 style={{ fontSize: 16, flex: 1 }}>{idea.title}</h2>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            {isOwner && !editing && <button className="btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => setEditing(true)}>Edit</button>}
            {editing && <button className="btn-primary" style={{ fontSize: 12, padding: '5px 12px' }} onClick={save} disabled={saving}>{saving ? '...' : 'Save'}</button>}
            {editing && <button className="btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => setEditing(false)}>Cancel</button>}
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
        </div>
        <div className="modal-body">
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            {editing ? (
              <select value={status} onChange={e => setStatus(e.target.value)} style={{ width: 140 }}>
                {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            ) : (
              <>
                <span style={{ fontSize: 12, fontWeight: 700, background: ss.bg, color: ss.color, padding: '3px 10px', borderRadius: 20 }}>{ss.label}</span>
                {idea.category && idea.category.split(',').map(c => (
                  <span key={c} style={{ fontSize: 11, background: 'var(--accent-dim)', color: 'var(--accent)', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>{c.trim()}</span>
                ))}
                <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 'auto' }}>by {idea.created_by_name} · {fmtDate(idea.created_at)}</span>
              </>
            )}
          </div>

          {editing && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ marginBottom: 8, display: 'block' }}>Category</label>
              <CategoryPicker value={category} onChange={setCategory} categories={categories} />
            </div>
          )}

          {editing ? (
            <textarea rows={6} value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe the idea..." style={{ marginBottom: 16, resize: 'vertical', lineHeight: 1.7 }} />
          ) : (
            idea.description && (
              <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 20 }}>
                <p style={{ fontSize: 13, color: 'var(--text)', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{idea.description}</p>
              </div>
            )
          )}

          {!editing && isOwner && (
            <div style={{ marginBottom: 20 }}>
              <button className="btn-ghost" style={{ fontSize: 12, padding: '7px 14px' }} onClick={() => onCreateTask(idea)}>✓ Convert to Task</button> <button className="btn-danger" onClick={onDelete}>Delete</button>
            </div>
          )}

          <hr className="divider" />
          <CommentsPanel ideaId={idea.id} user={user} />
        </div>
      </div>
    </div>
  );
}

function NewIdeaModal({ user, onClose, onSaved, categories = [], initialStatus="Draft" }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) return;
    setSaving(true);
    const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Unknown';
    await supabase.from('ideas').insert({ status:initialStatus, title: title.trim(), description: description || null, category: category || null, created_by: user?.id, created_by_name: userName });
    onSaved();
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2>New Idea</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div><label>Title *</label><input value={title} onChange={e => setTitle(e.target.value)} placeholder="What's the idea?" autoFocus /></div>
          </div>
          <div className="form-row">
            <div>
              <label>Category</label>
              <CategoryPicker value={category} onChange={setCategory} categories={categories} />
            </div>
          </div>
          <div className="form-row">
            <div><label>Description</label><textarea rows={5} value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe the idea in detail..." style={{ resize: 'vertical', lineHeight: 1.7 }} /></div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={saving || !title.trim()}>{saving ? 'Saving...' : 'Add Idea'}</button>
        </div>
      </div>
    </div>
  );
}

function IdeaCard({idea,onOpen,isOwner,onStatusChange,dragging,setDragging,pending}) {
 const done=['Done','Parked'].includes(idea.status);
 return <article className={`kanban-card ${done?'is-complete':''} ${dragging?.id===idea.id?'is-dragging':''}`} data-status={idea.status} draggable={isOwner&&!pending} onDragStart={e=>{e.dataTransfer.setData('ideaId',idea.id);setDragging(idea);}} onDragEnd={()=>setDragging(null)} aria-busy={pending}>
 <button className="kanban-card-open" onClick={()=>onOpen(idea)}>{!done&&<div className="kanban-context">Liongate{idea.category?' / '+idea.category:''}</div>}<h3>{done&&<span className="kanban-done-icon">{idea.status==='Parked'?'×':'✓'}</span>}{idea.title}</h3>{!done&&<>{idea.description&&<p>{idea.description}</p>}<div className="kanban-card-meta"><span className="kanban-owner"><b>{(idea.created_by_name||'?').charAt(0).toUpperCase()}</b>{idea.created_by_name||'Unassigned'}</span><time>{fmtDate(idea.created_at)}</time></div></>}</button>
 {!done&&isOwner&&<div className="kanban-card-actions"><button className="kanban-complete" disabled={pending} onClick={()=>onStatusChange(idea.id,'Done')} aria-label={`Complete ${idea.title}`}>✓</button><StatusMenu task={idea} columns={STATUSES} onChange={onStatusChange} disabled={pending}/></div>}
 </article>;
}

export default function IdeasPage({ user, isAdmin }) {
  const [ideas, setIdeas] = useState([]);
  const boardRef=useRef(null);
  const [search,setSearch]=useState(''),[error,setError]=useState(''),[pending,setPending]=useState(null);
  const [loading, setLoading] = useState(true);
  const [newModal, setNewModal] = useState(false);
  const [newStatus,setNewStatus]=useState("Draft");
  const [detailIdea, setDetailIdea] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [taskModal, setTaskModal] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [categories, setCategories] = useState([]);
  const [dragOver, setDragOver] = useState(null);

  const load = async () => {
    const { data,error } = await supabase.from('ideas').select('*').order('created_at', { ascending: false });
    if(error){setError(error.message);setLoading(false);return;}setError('');
    setIdeas(data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    supabase.from('settings').select('value').eq('key', 'idea_categories').single().then(({ data }) => {
      if (data?.value) setCategories(data.value);
    });
  }, []);

  const deleteIdea = async (id) => {
    if (!window.confirm('Delete this idea?')) return;
    await supabase.from('ideas').delete().eq('id', id); load();
  };

  const changeStatus = async (id,status) => {
    const idea=ideas.find(i=>i.id===id);if(!idea||pending||idea.status===status||!(idea.created_by===user?.id||isAdmin))return;
    setPending(id);setError('');
    try {const {data,error}=await supabase.from('ideas').update({status,updated_at:new Date().toISOString()}).eq('id',id).eq('updated_at',idea.updated_at).select('*').single();if(error)throw error;setIdeas(prev=>prev.map(i=>i.id===id?data:i));}catch(e){setError('Status was not saved. Refresh and try again. '+e.message);}finally{setPending(null);}
  };
  const handleDrop=async(e,status)=>{e.preventDefault();const id=e.dataTransfer.getData('ideaId');setDragOver(null);setDragging(null);if(id)await changeStatus(id,status);};

  const filtered = ideas.filter(i => (!categoryFilter || i.category?.split(',').map(c=>c.trim()).includes(categoryFilter)) && (!search || [i.title,i.description,i.category,i.created_by_name].join(' ').toLowerCase().includes(search.toLowerCase())));
  const grouped = {};
  STATUSES.forEach(s => { grouped[s.key] = filtered.filter(i => i.status === s.key); });

  const allCats = [...new Set([...categories, ...ideas.flatMap(i => i.category ? i.category.split(',').map(c => c.trim()) : [])])].filter(Boolean);

  const handleCreateTask = (idea) => { setDetailIdea(null); setTaskModal(idea); };

  const createTaskFromIdea = async () => {
    if (!taskModal) return;
    const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Unknown';
    await supabase.from('tasks').insert({ title: taskModal.title, notes: taskModal.description || null, status: 'To Do', priority: 'Medium', created_by: user?.id, created_by_name: userName, visible_to: ['all'], tags: [] });
    await supabase.from('ideas').update({ status: 'Done', updated_at: new Date().toISOString() }).eq('id', taskModal.id);
    setTaskModal(null); load();
  };

  return (
    <div className="kanban-page ideas-kanban">
      <div className="kanban-heading"><div><h1>Ideas</h1><p>{ideas.length} ideas · {ideas.filter(i=>i.status==='Active').length} active</p></div><button className="btn-primary" onClick={()=>{setNewStatus("Draft");setNewModal(true);}}>+ New Idea</button></div>
      <div className="crm-task-filters" style={{display:'flex',gap:10,marginBottom:20}}><select aria-label="Filter ideas by category" value={categoryFilter} onChange={e=>setCategoryFilter(e.target.value)}><option value="">All categories</option>{allCats.map(c=><option key={c}>{c}</option>)}</select></div>
      <div className="kanban-toolbar"><span className="kanban-view">▦ Board <small>{filtered.length}</small></span><input aria-label="Search ideas" placeholder="Search ideas…" value={search} onChange={e=>setSearch(e.target.value)}/><button className="kanban-refresh" onClick={load} aria-label="Refresh ideas">↻</button></div>
      <div className="kanban-navigation"><span>Drag to move · Open a card for details</span><div><button aria-label="Scroll board left" onClick={()=>boardRef.current?.scrollBy({left:-320,behavior:'smooth'})}>←</button><button aria-label="Scroll board right" onClick={()=>boardRef.current?.scrollBy({left:320,behavior:'smooth'})}>→</button><button onClick={()=>boardRef.current?.querySelector('[data-status="Done"]')?.scrollIntoView({behavior:'smooth',block:'nearest',inline:'end'})}>Go to completed</button></div></div>
      {error&&<p role="alert" className="ops-error">{error}</p>}
      {loading?<p>Loading…</p>:<div className="kanban-board" ref={boardRef} onDragOver={e=>{if(!dragging)return;const el=boardRef.current,r=el.getBoundingClientRect();if(e.clientX>r.right-70)el.scrollLeft+=24;else if(e.clientX<r.left+70)el.scrollLeft-=24;}}>{STATUSES.map(col=><section key={col.key} className={`kanban-column ${dragOver===col.key?'is-drop-target':''}`} data-status={col.key} onDragOver={e=>{if(dragging){e.preventDefault();setDragOver(col.key);}}} onDragLeave={()=>setDragOver(null)} onDrop={e=>handleDrop(e,col.key)}><div className="kanban-column-title"><span className="crm-stage">{col.label}</span><small>{grouped[col.key].length}</small><button aria-label={`Add idea to ${col.label}`} onClick={()=>{setNewStatus(col.key);setNewModal(true);}}>+</button></div><div className="kanban-card-list">{grouped[col.key].map(idea=><IdeaCard key={idea.id} idea={idea} onOpen={setDetailIdea} isOwner={idea.created_by===user?.id||isAdmin} onStatusChange={changeStatus} dragging={dragging} setDragging={setDragging} pending={pending===idea.id}/>)}{!grouped[col.key].length&&<div className="kanban-empty">No ideas here</div>}</div></section>)}</div>}

      {newModal && <NewIdeaModal user={user} categories={categories} initialStatus={newStatus} onClose={() => setNewModal(false)} onSaved={() => { setNewModal(false); load(); }} />}

      {detailIdea && (
        <IdeaDetailModal idea={detailIdea} user={user} isAdmin={isAdmin} categories={categories}
          onClose={() => setDetailIdea(null)}
          onRefresh={() => { setDetailIdea(null); load(); }}
          onCreateTask={handleCreateTask} onDelete={async()=>{await deleteIdea(detailIdea.id);setDetailIdea(null);}}
        />
      )}

      {taskModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setTaskModal(null)}>
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h2>Convert to Task</h2>
              <button className="close-btn" onClick={() => setTaskModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>This will create a new task from <strong>"{taskModal.title}"</strong> and mark the idea as Done.</p>
              <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '12px 14px', fontSize: 13, color: 'var(--text3)' }}>The task will be added to your To Do list.</div>
            </div>
            <div className="modal-footer">
              <button className="btn-ghost" onClick={() => setTaskModal(null)}>Cancel</button>
              <button className="btn-primary" onClick={createTaskFromIdea}>Create Task</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
